import type { AdminUserView, WorkflowUserView } from "@webuild-ai/admin/models/components";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Divider,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  User as UserAvatar,
  addToast,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { WorkflowOutput } from "@webuild-ai/admin/models/components";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useAdminStore } from "@/stores/AdminStore";
import { logger } from "@/telemetry";

interface WorkflowAccessPanelProps {
  workflow: WorkflowOutput;
  allUsers: AdminUserView[];
  onClose: () => void;
}

type PendingAdd = { userId: string; accessLevel: "Reader" | "Editor" };
type PendingChange = { userId: string; newLevel: "Reader" | "Editor" };

export const WorkflowAccessPanel: React.FC<WorkflowAccessPanelProps> = ({
  workflow,
  allUsers,
  onClose,
}) => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const {
    loadWorkflowUsers: fetchWorkflowUsers,
    addUserToWorkflow,
    removeUserFromWorkflow,
    changeWorkflowUserAccess,
  } = useAdminStore();

  const [currentUsers, setCurrentUsers] = useState<WorkflowUserView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [addSearch, setAddSearch] = useState("");

  // Batch change tracking
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<string[]>([]);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const hasUnsavedChanges =
    pendingAdds.length > 0 || pendingRemoves.length > 0 || pendingChanges.length > 0;

  const loadWorkflowUsers = useCallback(async () => {
    if (!adminClient || !workflow.id) return;
    setIsLoading(true);
    try {
      const data = await fetchWorkflowUsers(adminClient, workflow.id);

      setCurrentUsers(data.users);
    } catch (error) {
      logger.error("Failed to fetch workflow users", error instanceof Error ? error : undefined);
    } finally {
      setIsLoading(false);
    }
  }, [adminClient, workflow.id, fetchWorkflowUsers]);

  useEffect(() => {
    loadWorkflowUsers();
  }, [loadWorkflowUsers]);

  const adminUsers = useMemo(() => allUsers.filter((u) => u.role === "Admin"), [allUsers]);

  const currentUserIds = useMemo(() => {
    const ids = new Set(currentUsers.map((u) => u.id));

    pendingAdds.forEach((a) => ids.add(a.userId));
    pendingRemoves.forEach((id) => ids.delete(id));

    return ids;
  }, [currentUsers, pendingAdds, pendingRemoves]);

  const availableUsers = useMemo(() => {
    return allUsers
      .filter((u) => u.role !== "Admin")
      .filter((u) => !currentUserIds.has(u.id))
      .filter(
        (u) =>
          !addSearch ||
          u.name.toLowerCase().includes(addSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(addSearch.toLowerCase()),
      );
  }, [allUsers, currentUserIds, addSearch]);

  const handleAddUser = (userId: string) => {
    if (pendingRemoves.includes(userId)) {
      setPendingRemoves((prev) => prev.filter((id) => id !== userId));

      return;
    }
    setPendingAdds((prev) => [...prev, { userId, accessLevel: "Reader" }]);
    setAddSearch("");
  };

  const handleRemoveUser = (userId: string) => {
    const isPendingAdd = pendingAdds.find((a) => a.userId === userId);

    if (isPendingAdd) {
      setPendingAdds((prev) => prev.filter((a) => a.userId !== userId));

      return;
    }
    setPendingRemoves((prev) => [...prev, userId]);
  };

  const handleAccessChange = (userId: string, newLevel: "Reader" | "Editor") => {
    const isPendingAdd = pendingAdds.find((a) => a.userId === userId);

    if (isPendingAdd) {
      setPendingAdds((prev) =>
        prev.map((a) => (a.userId === userId ? { ...a, accessLevel: newLevel } : a)),
      );

      return;
    }
    const existing = currentUsers.find((u) => u.id === userId);

    if (existing && existing.accessLevel === newLevel) {
      setPendingChanges((prev) => prev.filter((c) => c.userId !== userId));

      return;
    }
    setPendingChanges((prev) => {
      const filtered = prev.filter((c) => c.userId !== userId);

      return [...filtered, { userId, newLevel }];
    });
  };

  const handleSave = async () => {
    if (!adminClient || !workflow.id) return;
    setIsSaving(true);

    try {
      const operations: Promise<void>[] = [];

      for (const add of pendingAdds) {
        operations.push(addUserToWorkflow(adminClient, workflow.id, add.userId, add.accessLevel));
      }
      for (const userId of pendingRemoves) {
        operations.push(removeUserFromWorkflow(adminClient, workflow.id, userId));
      }
      for (const change of pendingChanges) {
        operations.push(
          changeWorkflowUserAccess(adminClient, workflow.id, change.userId, change.newLevel),
        );
      }

      await Promise.all(operations);

      addToast({
        title: t("Changes saved"),
        description: t("Workflow access has been updated."),
        color: "success",
      });

      setPendingAdds([]);
      setPendingRemoves([]);
      setPendingChanges([]);
      await loadWorkflowUsers();
    } catch (error) {
      logger.error("Failed to save workflow access", error instanceof Error ? error : undefined);
      addToast({
        title: t("Error"),
        description:
          error instanceof Error ? error.message : t("Failed to save changes. Please try again."),
        color: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardModal(true);

      return;
    }
    onClose();
  };

  const getEffectiveAccessLevel = (userId: string, original: string) => {
    const change = pendingChanges.find((c) => c.userId === userId);

    return change ? change.newLevel : original;
  };

  const resolveUser = (userId: string) => allUsers.find((u) => u.id === userId);

  const userCount = currentUsers.length + pendingAdds.length - pendingRemoves.length;

  return (
    <div className="w-full lg:w-[480px] lg:min-w-[480px] border-l border-default-200 bg-default-50 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-default-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100">
              <Icon className="text-primary" icon="solar:widget-2-bold" width={20} />
            </div>
            <div>
              <h3 className="font-semibold text-lg leading-tight">
                {workflow.name || workflow.id}
              </h3>
              <div className="flex items-center gap-3 text-xs text-default-400 mt-0.5">
                <span>
                  {userCount} {userCount === 1 ? t("user") : t("users")}
                </span>
              </div>
            </div>
          </div>
          <Button isIconOnly size="sm" variant="light" onPress={handleClose}>
            <Icon icon="solar:close-circle-outline" width={20} />
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Admin info banner */}
            {adminUsers.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-warning-50 rounded-lg text-xs text-warning-700">
                <Icon className="shrink-0" icon="solar:info-circle-outline" width={16} />
                {t("{{count}} Admin(s) have unrestricted access and are not listed below.", {
                  count: adminUsers.length,
                })}
              </div>
            )}

            {/* Search users to add */}
            <div className="bg-default-100 rounded-lg p-3 space-y-3">
              <h4 className="text-xs font-semibold uppercase text-default-500">
                {t("Search Users to Add")}
              </h4>
              <Input
                placeholder={t("Search by name or email...")}
                size="sm"
                startContent={<Icon icon="solar:magnifer-outline" width={16} />}
                value={addSearch}
                onValueChange={setAddSearch}
              />

              {addSearch && availableUsers.length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {availableUsers.slice(0, 10).map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-default-200 transition-colors"
                    >
                      <UserAvatar
                        avatarProps={{ src: user.picture, size: "sm" }}
                        classNames={{ description: "text-xs" }}
                        description={user.email}
                        name={user.name}
                      />
                      <Button
                        color="primary"
                        size="sm"
                        variant="flat"
                        onPress={() => handleAddUser(user.id)}
                      >
                        {t("Add")}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {addSearch && availableUsers.length === 0 && (
                <p className="text-xs text-default-400">{t("No users found")}</p>
              )}
            </div>

            <Divider />

            {/* Approved Users */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold uppercase text-default-500">
                  {t("Approved Users")}
                </h4>
                {hasUnsavedChanges && (
                  <Chip color="warning" size="sm" variant="flat">
                    {t("Unsaved")}
                  </Chip>
                )}
              </div>

              <div className="space-y-1">
                {/* Existing users (not pending removal) */}
                {currentUsers
                  .filter((u) => !pendingRemoves.includes(u.id))
                  .map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-default-100"
                    >
                      <UserAvatar
                        avatarProps={{ src: user.picture, size: "sm" }}
                        classNames={{ description: "text-xs" }}
                        description={user.email}
                        name={user.name}
                      />
                      <div className="flex items-center gap-2">
                        <Select
                          aria-label={t("Access level")}
                          className="w-28"
                          selectedKeys={[getEffectiveAccessLevel(user.id, user.accessLevel)]}
                          size="sm"
                          variant="flat"
                          onChange={(e) =>
                            handleAccessChange(user.id, e.target.value as "Reader" | "Editor")
                          }
                        >
                          <SelectItem key="Reader">{t("Reader")}</SelectItem>
                          <SelectItem key="Editor">{t("Editor")}</SelectItem>
                        </Select>
                        <Button
                          isIconOnly
                          color="danger"
                          size="sm"
                          variant="light"
                          onPress={() => handleRemoveUser(user.id)}
                        >
                          <Icon icon="solar:trash-bin-minimalistic-outline" width={16} />
                        </Button>
                      </div>
                    </div>
                  ))}

                {/* Pending adds */}
                {pendingAdds.map((add) => {
                  const user = resolveUser(add.userId);

                  if (!user) return null;

                  return (
                    <div
                      key={add.userId}
                      className="flex items-center justify-between p-2 rounded-lg bg-success-50"
                    >
                      <div className="flex items-center gap-2">
                        <Chip color="success" size="sm" variant="flat">
                          {t("New")}
                        </Chip>
                        <UserAvatar
                          avatarProps={{ src: user.picture, size: "sm" }}
                          classNames={{ description: "text-xs" }}
                          description={user.email}
                          name={user.name}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          aria-label={t("Access level")}
                          className="w-28"
                          selectedKeys={[add.accessLevel]}
                          size="sm"
                          variant="flat"
                          onChange={(e) =>
                            handleAccessChange(add.userId, e.target.value as "Reader" | "Editor")
                          }
                        >
                          <SelectItem key="Reader">{t("Reader")}</SelectItem>
                          <SelectItem key="Editor">{t("Editor")}</SelectItem>
                        </Select>
                        <Button
                          isIconOnly
                          color="danger"
                          size="sm"
                          variant="light"
                          onPress={() => handleRemoveUser(add.userId)}
                        >
                          <Icon icon="solar:trash-bin-minimalistic-outline" width={16} />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {/* Pending removes (shown as greyed out) */}
                {pendingRemoves.map((userId) => {
                  const user = currentUsers.find((u) => u.id === userId);

                  if (!user) return null;

                  return (
                    <div
                      key={userId}
                      className="flex items-center justify-between p-2 rounded-lg bg-danger-50 opacity-60"
                    >
                      <UserAvatar
                        avatarProps={{ src: user.picture, size: "sm" }}
                        classNames={{ description: "text-xs" }}
                        description={user.email}
                        name={user.name}
                      />
                      <Button
                        color="warning"
                        size="sm"
                        variant="flat"
                        onPress={() =>
                          setPendingRemoves((prev) => prev.filter((id) => id !== userId))
                        }
                      >
                        {t("Undo")}
                      </Button>
                    </div>
                  );
                })}

                {currentUsers.length === 0 && pendingAdds.length === 0 && (
                  <p className="text-sm text-default-400 text-center py-4">
                    {t("No users assigned to this workflow yet.")}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-default-200 flex justify-end">
        <Button
          color="primary"
          isDisabled={!hasUnsavedChanges}
          isLoading={isSaving}
          startContent={<Icon icon="solar:diskette-outline" width={18} />}
          onPress={handleSave}
        >
          {t("Save Changes")}
        </Button>
      </div>

      {/* Discard unsaved changes confirmation */}
      <Modal isOpen={showDiscardModal} onClose={() => setShowDiscardModal(false)}>
        <ModalContent>
          <ModalHeader>{t("Unsaved Changes")}</ModalHeader>
          <ModalBody>
            <p>{t("You have unsaved changes. Are you sure you want to close?")}</p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setShowDiscardModal(false)}>
              {t("Cancel")}
            </Button>
            <Button
              color="danger"
              onPress={() => {
                setShowDiscardModal(false);
                onClose();
              }}
            >
              {t("Discard")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
